import { API_BASE_URL } from '../config';

export const runAIGeneration = async (prompt: string, token: string | null): Promise<string> => {
  if (!token) {
    const errorMessage = 'Authentication token is missing. Cannot call AI service.';
    console.error(`💥 [AI Service] Fatal error: ${errorMessage}`);
    throw new Error(errorMessage);
  }

  try {
    console.log('🚀 [AI Service] Starting AI generation request...');
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    const response = await fetch(`${API_BASE_URL}/api/ai/generate`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({ prompt }),
    });

    console.log('📊 [AI Service] Response status:', response.status, response.statusText);
    console.log('📋 [AI Service] Response headers:', {
      contentType: response.headers.get('content-type'),
      contentLength: response.headers.get('content-length'),
    });

    if (!response.ok) {
      console.error('❌ [AI Service] HTTP Error:', response.status);
      const errorText = await response.text();

      try {
        const errorData = JSON.parse(errorText);
        console.error('🔴 [AI Service] Backend error response (from JSON):', errorData);
        const errorMsg = errorData.error || errorData.message || `HTTP ${response.status}`;
        const details = errorData.details ? ` - ${errorData.details}` : '';
        throw new Error(`${errorMsg}${details}`);
      } catch (jsonParseErr) {
        console.error('🔴 [AI Service] Backend error response (raw text):', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }
    }

    const responseText = await response.text();
    let text = '';

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
        try {
            const parsedData = JSON.parse(responseText);
            console.log('📦 [AI Service] Parsed JSON response data:', parsedData);

            if (parsedData && typeof parsedData.text === 'string') {
                text = parsedData.text;
                console.log('✨ [AI Service] Extracted text from response wrapper:', text.substring(0, 100));
            } else if (typeof parsedData === 'string') {
                text = parsedData;
                console.log('✨ [AI Service] Response was direct string:', text.substring(0, 100));
            } else {
                console.warn('⚠️ [AI Service] Unexpected JSON structure, stringifying as fallback:', parsedData);
                text = JSON.stringify(parsedData, null, 2);
            }
        } catch (jsonErr) {
            console.warn('⚠️ [AI Service] JSON parse failed, falling back to raw text:', jsonErr);
            text = responseText;
        }
    } else {
        console.log('📋 [AI Service] Response is not JSON, using as plain text.');
        text = responseText;
    }

    if (!text || text.trim().length === 0) {
      console.error('❌ [AI Service] Received empty response');
      throw new Error('AI service returned empty content');
    }

    console.log('🎉 [AI Service] Successfully processed response. Length:', text.length);
    return text;

  } catch (error) {
    console.error('💥 [AI Service] Fatal error:', {
      message: error instanceof Error ? error.message : String(error),
      type: error instanceof Error ? error.constructor.name : typeof error,
      fullError: error
    });

    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`Unknown error during AI generation: ${String(error)}`);
  }
};